import * as React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Activity, BarChart3, Bell, ChevronLeft, ChevronRight, Database, Gauge, Globe, LayoutDashboard,
  Building2, Languages, LogOut, Menu, Search, Settings, ShieldAlert, Sparkles, Users, X,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useDeliveries } from '@/lib/queries'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface NavItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  /** Second-level entries, rendered when the section is active. */
  children?: Array<{ to: string; label: string; adminOnly?: boolean }>
  adminOnly?: boolean
  /** Phase-2 capabilities are shown disabled rather than hidden (§5.2). */
  comingSoon?: boolean
}

const NAV: NavItem[] = [
  { to: '/', label: '概览', icon: LayoutDashboard },
  {
    to: '/domains',
    label: '域名库',
    icon: Database,
    // No sub-entries: 可注册 and 即将删除 were the status filter spelled a
    // second way, and keeping both meant the same view had two addresses that
    // could not agree once you touched the filters. 已关注 moved to the
    // toolbar, where it sits next to the filters it composes with.
  },
  { to: '/registrars', label: '注册商', icon: Building2 },
  { to: '/semantics', label: '语义库', icon: Languages },
  { to: '/query', label: '域名查询', icon: Search },
  {
    to: '/rules',
    label: '规则管理',
    icon: Sparkles,
    children: [
      // 我的规则 became 域名规则: it is the domain rule list, and naming it that
      // is what makes 标签规则 beside it read as a peer rather than a sub-item.
      { to: '/rules', label: '域名规则' },
      { to: '/tag-rules', label: '标签规则', adminOnly: true },
    ],
  },
  {
    to: '/monitoring',
    label: '监控',
    icon: Activity,
  },
  { to: '/events', label: '生命周期事件', icon: Globe },
  { to: '/release-analytics', label: '释放统计', icon: BarChart3 },
  { to: '/notifications', label: '通知', icon: Bell },
  {
    to: '/registration',
    label: '自动注册',
    icon: Gauge,
    children: [
      { to: '/registration', label: '注册单与额度' },
      { to: '/registrar-providers', label: '注册商接口', adminOnly: true },
      { to: '/registrar-calls', label: '接口调用日志', adminOnly: true },
    ],
  },
  { to: '/users', label: '用户管理', icon: Users, adminOnly: true },
  { to: '/admin', label: '系统运维', icon: ShieldAlert, adminOnly: true },
  { to: '/settings', label: '系统设置', icon: Settings },
]

const COLLAPSE_KEY = 'ndm.sidebar.collapsed'

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, entitlements, logout } = useAuth()
  const location = useLocation()
  const [collapsed, setCollapsed] = React.useState(
    () => localStorage.getItem(COLLAPSE_KEY) === '1',
  )
  const [mobileOpen, setMobileOpen] = React.useState(false)

  React.useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  // Navigating closes the mobile drawer; otherwise it covers the page you
  // just asked for.
  React.useEffect(() => setMobileOpen(false), [location.pathname, location.search])

  const { data: deliveries } = useDeliveries({ limit: 1 })
  const unread = deliveries?.unread ?? 0

  // Filtered at both levels: 规则管理 itself is for everyone, but only an admin
  // can edit tag rules, and a menu entry whose every button returns 403 is worse
  // than no entry at all.
  const visibleNav = NAV
    .filter((item) => !item.adminOnly || user?.role === 'admin')
    .map((item) => (item.children
      ? { ...item, children: item.children.filter((c) => !c.adminOnly || user?.role === 'admin') }
      : item))

  return (
    <div className="flex min-h-screen bg-muted/30">
      {/* Skip link: the first tab stop on every page (§5.8). */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:shadow-lg"
      >
        跳到主内容
      </a>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r bg-background transition-[width,transform] duration-200',
          collapsed ? 'w-16' : 'w-60',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        aria-label="主导航"
      >
        <div className="flex h-14 items-center gap-2 border-b px-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Globe className="h-4 w-4" aria-hidden />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">域名发现与监控</p>
              <p className="truncate text-[11px] text-muted-foreground">MVP · 非注册商平台</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="关闭导航"
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {visibleNav.map((item) => (
            <NavEntry key={item.to} item={item} collapsed={collapsed} />
          ))}
        </nav>

        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className={cn('w-full', collapsed ? 'justify-center px-0' : 'justify-start')}
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
          >
            <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} aria-hidden />
            {!collapsed && <span>收起</span>}
          </Button>
        </div>
      </aside>

      <div className={cn('flex min-w-0 flex-1 flex-col transition-[padding] duration-200', collapsed ? 'lg:pl-16' : 'lg:pl-60')}>
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur">
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="打开导航"
          >
            <Menu className="h-4 w-4" aria-hidden />
          </Button>

          <div className="min-w-0 flex-1" />

          {entitlements && (
            <Tooltip
              content={
                entitlements.plan_code === 'free'
                  ? '免费层：监控数量与通知时效受限，可在系统设置查看权益明细'
                  : `当前套餐：${entitlements.plan_name}`
              }
            >
              <Badge variant={entitlements.plan_code === 'free' ? 'muted' : 'default'}>
                {entitlements.plan_name}
              </Badge>
            </Tooltip>
          )}

          <NavLink
            to="/notifications"
            className="relative rounded-md p-2 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={unread > 0 ? `通知（${unread} 条未读）` : '通知'}
          >
            <Bell className="h-4 w-4" aria-hidden />
            {unread > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </NavLink>

          <div className="flex items-center gap-2 border-l pl-3">
            <div className="hidden text-right sm:block">
              <p className="text-xs font-medium leading-tight">{user?.display_name}</p>
              <p className="text-[11px] leading-tight text-muted-foreground">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon-sm" onClick={() => void logout()} aria-label="退出登录">
              <LogOut className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </header>

        <main id="main-content" className="min-w-0 flex-1 p-4 lg:p-6" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  )
}

function NavEntry({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation()
  const Icon = item.icon
  const ownRouteActive = navTargetMatches(location.pathname, location.search, item.to)
  // Some children are sibling routes rather than nested paths (for example,
  // /registrar-providers belongs to /registration). The parent section must
  // stay active for every child route or navigating to a child collapses the
  // very menu needed to move between them.
  const isActive = ownRouteActive || Boolean(
    item.children?.some((child) => navTargetMatches(location.pathname, location.search, child.to)),
  )
  const [expanded, setExpanded] = React.useState(isActive)

  // Route changes establish the sensible default: the current section opens
  // and sections left behind close. A click without navigation can still
  // override this state, which is what lets the active section collapse.
  React.useEffect(() => setExpanded(isActive), [location.pathname, location.search, isActive])

  if (item.comingSoon) {
    // §5.2: phase-2 features are visible but clearly unavailable, so the
    // roadmap is legible without pretending the feature exists.
    return (
      <Tooltip content="自动注册为二期能力，MVP 不提供自动扣费注册">
        <div
          className={cn(
            'flex cursor-not-allowed items-center gap-2 rounded-md px-2 py-2 text-sm text-muted-foreground/60',
            collapsed && 'justify-center px-0',
          )}
          aria-disabled="true"
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          {!collapsed && (
            <>
              <span className="flex-1 truncate">{item.label}</span>
              <Badge variant="outline" className="text-[10px]">即将推出</Badge>
            </>
          )}
        </div>
      </Tooltip>
    )
  }

  const link = (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      aria-current={ownRouteActive ? 'page' : undefined}
      aria-expanded={item.children ? expanded : undefined}
      onClick={(event) => {
        if (!item.children) return
        if (isActive) {
          event.preventDefault()
          setExpanded((value) => !value)
        } else {
          setExpanded(true)
        }
      }}
      className={() =>
        cn(
          'flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isActive
            ? 'bg-primary/10 font-medium text-primary'
            : 'text-foreground/80 hover:bg-accent',
          collapsed && 'justify-center px-0',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.children && (
            <ChevronRight
              className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-90 text-foreground')}
              aria-hidden
            />
          )}
        </>
      )}
    </NavLink>
  )

  return (
    <div>
      {collapsed ? (
        <Tooltip content={item.children ? `${item.label}（${item.children.length} 个子页面）` : item.label} side="right">
          {link}
        </Tooltip>
      ) : link}
      {!collapsed && expanded && item.children && (
        <div className="ml-[15px] mt-0.5 space-y-0.5 border-l border-border pl-[17px]">
          {item.children.map((child) => {
            const active = navTargetMatches(location.pathname, location.search, child.to)
            return (
              <NavLink
                key={child.to}
                to={child.to}
                className={cn(
                  'relative block rounded-md px-2 py-1.5 text-xs transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                <span
                  className={cn(
                    'absolute -left-[18px] top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-muted-foreground/40 bg-background',
                    active && 'border-primary bg-primary',
                  )}
                  aria-hidden
                />
                {child.label}
              </NavLink>
            )
          })}
        </div>
      )}
    </div>
  )
}

function navTargetMatches(pathname: string, search: string, target: string): boolean {
  const queryAt = target.indexOf('?')
  const targetPath = queryAt === -1 ? target : target.slice(0, queryAt)
  const targetSearch = queryAt === -1 ? '' : target.slice(queryAt)
  const pathMatches = targetPath === '/'
    ? pathname === '/'
    : pathname === targetPath || pathname.startsWith(`${targetPath}/`)
  return pathMatches && (!targetSearch || search === targetSearch)
}

/** Consistent page header used by every route. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description && <div className="mt-1 text-sm text-muted-foreground">{description}</div>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}
